import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { getActiveCommerceId, normalizeFeedbackOptions, writeCache } from '@utils/featureRollout/cache';
import { fetchFeatureVersion } from '@services/featureVersionService';
import {
  drainOutbox,
  enqueueOptOut,
  enqueueSnooze,
  enqueueSwitch,
  enqueueTelemetry,
} from '@utils/featureRollout/outbox';
import { applyFeatureVersionTags } from '@utils/featureRollout/instrumentation';
import { isPreviewActive } from '@utils/featureRollout/preview';
import { scheduleReconcile } from '@utils/featureRollout/reconcilers';
import {
  maybeRequestPermanenceSurveyForAdopted,
  requestPermanenceSurvey,
} from '@utils/featureRollout/permanenceSurvey';
import { isFeatureBuildEnabled } from '@utils/featureFlags';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { getStoreSnapshot, setStoreEntry, setStoreResolved, subscribeStore } from '@utils/featureRollout/store';
import { ulid } from '@utils/featureRollout/ulid';
import type {
  EvacReason,
  FeatureBehavior,
  FeatureMenuState,
  FeatureVariant,
  FeatureVersionCacheEntry,
  FeedbackOptionsCatalog,
  InviteSurface,
  ManualEntry,
} from '@utils/featureRollout/types';

// Triggers PUSH LEGACY de menú (modal/card del shell, call-sites previos al contrato
// FeatureGate). Las features nuevas NO se agregan aquí: el carácter push viaja como
// `opts.pushInvite` de switchTo (lo pasa accept() de makeFeatureGateHook) — una lista de
// strings reproduce el bug de "trigger olvidado" en cada feature nueva (SPEC-1).
function isPushTrigger(trigger: string | undefined): boolean {
  return trigger === 'menu_modal' || trigger === 'menu_shell';
}

// Mapea cada `behavior` de rollout a su evento GA4 del catálogo. `opt_out` no tiene evento
// en el catálogo → no se espeja (sin inventar nombres). `invitation_accepted` solo se emite
// en triggers PUSH (lo garantiza el call-site), así que mapearlo 1:1 respeta push-only.
const BEHAVIOR_TO_GA4: Partial<Record<FeatureBehavior, string>> = {
  invitation_shown: TRACK_EVENT_NAMES.FeatureInvitationShown,
  invitation_accepted: TRACK_EVENT_NAMES.FeatureInvitationAccepted,
  switched_to: TRACK_EVENT_NAMES.FeatureSwitched,
  reverted_to: TRACK_EVENT_NAMES.FeatureReverted,
  // Feedback del revert: evento propio (NO reverted_to) para no duplicar el conteo de reversiones.
  revert_feedback: TRACK_EVENT_NAMES.FeatureRevertFeedback,
};

export interface UseFeatureVersionResult {
  variant: FeatureVariant;
  canRevert: boolean;
  gaForced: boolean;
  menuState: FeatureMenuState | null;
  experimentId: string | null;
  // Superficie de invitación DERIVADA por el backend (cadencia server-side). El modal/card
  // OBEDECEN esto en vez de la heurística menu_state. Si operator_evacuated → forzamos 'none'
  // como defensa contra cache stale (el server ya manda 'none', esto cubre el race).
  inviteSurface: InviteSurface;
  // Canal PULL (chip "probar la versión nueva"). 'available' = el chip se muestra; 'hidden' =
  // GA | evacuado | opt-out explícito del chip (manual_entry_locked). Defensa anti-stale: si
  // operator_evacuated → forzamos 'hidden' (mismo patrón que inviteSurface). Es INDEPENDIENTE
  // de la cadencia PUSH (state/snooze/cooldown): el chip vive mientras manual_entry='available'.
  manualEntry: ManualEntry;
  // El operador evacuó este comercio fuera-de-bucket a control (ramp-down). No invitar y
  // mostrar copy tranquilizador ("volvimos temporalmente a la anterior… no perdiste nada").
  operatorEvacuated: boolean;
  // Motivo del ramp-down: 'iterating' (pausa temporal, copy A1) | 'discontinued' (cierre, copy
  // B2). Default 'iterating' si el backend no lo manda. Solo significativo con operatorEvacuated.
  evacReason: EvacReason;
  // Peek de comparación vigente: el FE muestra la barra sticky "estás comparando" y el chip
  // ofrece "volver a la nueva". Lo deriva el backend (compare_active).
  compareActive: boolean;
  // Catálogo de chips de feedback curado por CS en el ERP para el experimento activo (anexo
  // feedback_options). null = sin catálogo → el consumidor usa los defaults de strings.ts.
  // Claves independientes (puede venir solo revert o solo permanence). Ya viene NORMALIZADO
  // (inválidos/duplicados/'other' filtrados, cap 7): el render no re-valida.
  feedbackOptions: FeedbackOptionsCatalog | null;
  // Elegible para invitación = está en `control`, hay un experimento activo y la feature
  // no graduó a GA todavía. Es decir: podemos ofrecerle probar la nueva versión y aún no
  // la ha aceptado. Lo consume el badge del nav y el modal contextual de primera vez.
  isEligibleInvitation: boolean;
  // `resolved` = ya hidratamos desde el backend al menos una vez en esta sesión. El
  // render NO espera a que sea true: la variante sale del cache local (síncrono).
  resolved: boolean;
  // Cambia la variante. Optimista + fire-and-forget (PUT vía outbox). El handler del
  // click NUNCA hace await. No-root → el backend responde REQUIRES_ROOT; el FE no rompe.
  // Triggers de comparación (compare_peek/compare_return/revert_commit) ramifican intención
  // en el backoffice; el peek preserva can_revert y NO muta menu_state.
  // `opts.pushInvite` marca el switch como aceptación de invitación PUSH (emite
  // invitation_accepted) — lo pasan las superficies de invitación (accept() del FeatureGate).
  switchTo: (next: FeatureVariant, trigger?: string, opts?: { pushInvite?: boolean }) => void;
  // Azúcar: vuelve a 'control' (regresar a la versión anterior). Rechazo definitivo.
  revert: (trigger?: string) => void;
  // Envía el motivo del revert (prompt de feedback D1) como telemetría fire-and-forget. NO
  // cambia la variante (el revert ya ocurrió): solo adjunta el porqué al evento reverted_to.
  // Omitible: si el comercio no responde, nunca se llama. Si el backend aún no acepta `reason`,
  // viaja igual en el payload y degrada sin romper. `reason` es string ancho: con el catálogo
  // per-experimento (feedback_options) los slugs ya no son una union cerrada de strings.ts —
  // el value siempre viajó verbatim, el tipo estrecho era solo documentación.
  submitRevertFeedback: (reason: string, text?: string, trigger?: string) => void;
  // Envía la opinión del operador tras USAR la versión nueva (prompt por uso, E#3). Telemetría
  // fire-and-forget (behavior 'usage_feedback') con by_user_id + core_action_count en el payload.
  // NO cambia la variante ni cuenta como adopción/reversión. Omitible; el cap/opt-out los gobierna
  // el consumidor (preview.ts). El backend lo registra como behavior_event independiente.
  submitUsageFeedback: (reason: string, text?: string, coreActionCount?: number, trigger?: string) => void;
  // Adopción EXPLÍCITA ("Quedarme con la versión nueva"). En treatment, confirma la permanencia:
  // encola un switch a la MISMA variante (treatment) con trigger 'kept' por el outbox (PUT) — el
  // backoffice (PR #678) lo interpreta como adopción y pone state='adopted' + emite el evento
  // 'adopted'/'kept'. NO cambia la variante en pantalla (ya es treatment), por eso usa su propia
  // ruta y no switchTo (que early-returna cuando next===from). Fire-and-forget (nunca await en el
  // handler) e idempotente (event_id ULID estable por switch). Además dispara la encuesta de
  // permanencia. No-root degrada como el resto (el PUT vuelve REQUIRES_ROOT y el outbox lo revierte
  // sin V2 fantasma — aquí no hay variante que revertir).
  keepCurrent: (trigger?: string) => void;
  // Envía la opinión POSITIVA del comercio al adoptar/quedarse (encuesta de permanencia). Telemetría
  // fire-and-forget (behavior 'usage_feedback') con trigger_source ∈ {adopted, kept} + reason_text
  // NO-PII. NO cambia la variante ni cuenta como adopción/reversión (la adopción ya la registró el
  // disparador). Omitible.
  submitPermanenceFeedback: (reason: string, text?: string, trigger?: string) => void;
  // "No volver a preguntar": opt_out vía outbox (PUT behavior='opt_out'). El backend lo vuelve
  // sticky. No cambia la variante.
  optOut: (trigger?: string) => void;
  // "Recordármelo más tarde": snooze (PUT snooze_days). El backend suprime invitaciones hasta
  // now+days. No cambia la variante. Default 7 días.
  snooze: (days?: number, trigger?: string) => void;
  // Registra que la invitación se mostró (telemetría, una vez por sesión por feature).
  markInvitationShown: (trigger?: string) => void;
  // Registra un error de render/runtime de la variante treatment gateada (menu-v2). Fire-and-
  // forget: emite behavior='error' con experiment_id + variant actuales y un payload NO-PII
  // (nombre/mensaje del error, truncados). Es la fuente del guardrail max_error_rate_pct (Tier 3).
  // No rompe el render: lo invoca el error boundary del gate, que ya cayó al fallback/V1.
  emitError: (error: unknown, trigger?: string) => void;
}

function toEntry(
  featureKey: string,
  snapshot: {
    variant: FeatureVariant;
    can_revert: boolean;
    ga_forced: boolean;
    menu_state: FeatureMenuState | null;
    experiment_id: string | null;
    invite_surface: InviteSurface;
    manual_entry: ManualEntry;
    next_eligible_at: string | null;
    operator_evacuated: boolean;
    evac_reason: EvacReason;
    compare_active: boolean;
    // Catálogo de chips per-experimento (ya normalizado). Las escrituras optimistas lo PRESERVAN
    // del entry vigente (un switch local no cambia el catálogo); la hidratación lo re-normaliza.
    feedback_options: FeedbackOptionsCatalog | null;
    // Confirmación del backend. Las escrituras OPTIMISTAS (switchTo/optOut/snooze) pasan null:
    // aún no hay acuse del backend. La hidratación autoritativa pasa Date.now() al sellar.
    confirmed_at: number | null;
  },
): FeatureVersionCacheEntry {
  return {
    feature_key: featureKey,
    variant: snapshot.variant,
    can_revert: snapshot.can_revert,
    ga_forced: snapshot.ga_forced,
    menu_state: snapshot.menu_state,
    experiment_id: snapshot.experiment_id,
    invite_surface: snapshot.invite_surface,
    manual_entry: snapshot.manual_entry,
    next_eligible_at: snapshot.next_eligible_at,
    operator_evacuated: snapshot.operator_evacuated,
    evac_reason: snapshot.evac_reason,
    compare_active: snapshot.compare_active,
    feedback_options: snapshot.feedback_options,
    updated_at: Date.now(),
    confirmed_at: snapshot.confirmed_at,
  };
}

function normalizeInviteSurface(value: unknown): InviteSurface {
  if (value === 'modal' || value === 'card' || value === 'none') return value;
  return 'none';
}

// Default conservador 'hidden' si el backend no manda manual_entry (cache viejo). El server
// siempre emite 'available'/'hidden'; este fallback solo cubre respuestas pre-contrato.
function normalizeManualEntry(value: unknown): ManualEntry {
  return value === 'available' ? 'available' : 'hidden';
}

// Default robusto 'iterating' (pausa temporal) si el backend no manda evac_reason. El aviso de
// ramp-down cae al mensaje tranquilizador A1; 'discontinued' (B2) solo si el server lo emite.
function normalizeEvacReason(value: unknown): EvacReason {
  return value === 'discontinued' ? 'discontinued' : 'iterating';
}

// Evita doble fetch cuando hay varias instancias del hook montadas para el mismo feature
// (gate del router + card de invitación). La hidratación es responsabilidad de UNA sola.
const hydratingFor = new Set<string>();

// Reset de PRODUCCIÓN: limpia el guard de hidratación al cambiar de sesión/sucursal. Si un
// fetch quedó en vuelo al cambiar de comercio, el guard bloquearía la re-hidratación del nuevo;
// vaciarlo (junto con resetFeatureVersionStore) garantiza que el nuevo comercio re-resuelva.
export function resetFeatureVersionHydration(): void {
  hydratingFor.clear();
}

// Solo para tests: limpia el guard de hidratación entre casos.
export function __resetHydrationForTests(): void {
  hydratingFor.clear();
}

// Guard DURABLE de `invitation_shown`: una vez por sesión por (comercio, feature). Antes vivía en
// un useRef POR MONTAJE → cada remount del shell (navegar entre subpáginas del menú) reseteaba el
// guard y re-emitía el evento (visto en prod: 38 invitation_shown de un comercio en ~1h, badgering
// de telemetría). Un Set de módulo keyed por comercio+feature sobrevive a los remounts y solo se
// vacía al cambiar de sesión/sucursal (resetInvitationShown, junto al resto de resets del módulo).
const invitationShownFor = new Set<string>();

export function resetInvitationShown(): void {
  invitationShownFor.clear();
}

// Solo para tests: limpia el guard de invitación entre casos.
export function __resetInvitationShownForTests(): void {
  invitationShownFor.clear();
}

// Lee la variante del store de módulo (inicializado SÍNCRONO desde cache local, 0ms) y la
// hidrata en background. El store es compartido: TODAS las instancias del hook re-renderizan
// cuando cambia la variante, así el switch optimista de la card re-monta el subtree del menú
// (gate del router) en el mismo tick — sin navigate ni reload.
export function useFeatureVersion(featureKey: string): UseFeatureVersionResult {
  const state = useSyncExternalStore(
    (cb) => subscribeStore(featureKey, cb),
    () => getStoreSnapshot(featureKey),
    () => getStoreSnapshot(featureKey),
  );
  const { entry, resolved } = state;
  // Fan-out de los eventos de rollout a GA4 (además del outbox del backend).
  // useTracking devuelve trackEvent SIN memoizar (nueva referencia por render); lo guardamos en
  // un ref para no desestabilizar `emit` y, en cascada, switchTo/optOut/markInvitationShown que
  // dependen de emit. El ref siempre apunta al trackEvent más reciente. Reportado por: Seer.
  const { trackEvent } = useTracking();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  // event_id ESTABLE de la adopción explícita ('kept'), cacheado por commerce_id. keepCurrent puede
  // dispararse más de una vez para el mismo comercio (el CTA "Quedarme" sigue visible en treatment y
  // el popover se reabre): reusar el MISMO id hace que el dedupe del outbox (por id) colapse los
  // reencolados en uno → no se duplica el PUT 'kept' ni el evento 'switched_to/kept' (data quality
  // del dashboard de adopción). Per-commerce: cambiar de sucursal genera su propia adopción. Reportado por: Seer.
  const keptEventIdRef = useRef<Map<string, string>>(new Map());

  // Aplica los tags de observabilidad cada vez que cambia la variante/estado resueltos.
  // Pasa `feature_key` para que las dimensiones genéricas (rollout_variant/rollout_state)
  // queden sliceables por feature → 2+ features vivas no mezclan datos.
  useEffect(() => {
    applyFeatureVersionTags({ feature_key: featureKey, variant: entry.variant, menu_state: entry.menu_state });
  }, [featureKey, entry.variant, entry.menu_state]);

  // Hidratación en background (no se espera en el render). Reconciliación silenciosa contra
  // el store compartido. La ejecuta UNA sola instancia por featureKey (guard hydratingFor).
  useEffect(() => {
    // Guard de build-flag (BLOCKER de deploy): si la feature NO está habilitada en esta build
    // (p.ej. menu-v2 OFF en prod), el hook queda INERTE — sin GET /v1/feature-version y sin
    // drenar el outbox. Evita que toda la base genere requests/telemetría fallidos si el FE
    // llega a prod antes que api+panel+backoffice. El render sigue funcionando con el fallback
    // síncrono `control`.
    if (!isFeatureBuildEnabled(featureKey)) return;
    // Hidrata una sola vez por featureKey (ya resuelto → no re-hidratar). NO usamos un flag
    // `cancelled` + cleanup: en StrictMode (dev) el desmontaje cancelaba el primer fetch y el
    // guard bloqueaba el segundo, dejando el store sin reconciliar. El store es de módulo, así
    // que aplicar el resultado tras un desmontaje es correcto y necesario.
    if (hydratingFor.has(featureKey) || getStoreSnapshot(featureKey).resolved) return;
    hydratingFor.add(featureKey);
    const commerceId = getActiveCommerceId();
    drainOutbox(commerceId).catch(() => {
      // fire-and-forget: drenar el outbox no debe afectar el render
    });

    fetchFeatureVersion(featureKey)
      .then((res) => {
        // El GET es AUTORITATIVO: el resultado del backend GANA sobre cualquier cache optimista.
        // Esto corrige el bug de "V2 en pantalla / control en BD" en sus dos direcciones:
        //   (a) cache=treatment sin confirmar pero backend=control → el FE baja a control.
        //   (b) backend=treatment (switch confirmado O auto-hook forced_to_treatment sin switch
        //       local) → el FE aprende treatment aunque nunca hubo switch en este dispositivo.
        // Sellamos confirmed_at=Date.now(): a partir de aquí la variante está respaldada por la BD.
        const next = toEntry(featureKey, {
          variant: res.variant === 'treatment' ? 'treatment' : 'control',
          can_revert: Boolean(res.can_revert),
          ga_forced: Boolean(res.ga_forced),
          menu_state: (res.menu_state as FeatureMenuState | null) ?? null,
          experiment_id: res.experiment_id ?? null,
          invite_surface: normalizeInviteSurface(res.invite_surface),
          manual_entry: normalizeManualEntry(res.manual_entry),
          next_eligible_at: res.next_eligible_at ?? null,
          operator_evacuated: Boolean(res.operator_evacuated),
          evac_reason: normalizeEvacReason(res.evac_reason),
          compare_active: Boolean(res.compare_active),
          // Catálogo per-experimento: normalización defensiva compartida con readCache. Malformado
          // o ausente → null → defaults de strings.ts (nunca rompe el prompt ni el envío).
          feedback_options: normalizeFeedbackOptions(res.feedback_options),
          confirmed_at: Date.now(),
        });
        const prevEntry = getStoreSnapshot(featureKey).entry;
        writeCache(next, commerceId);
        setStoreEntry(featureKey, next);
        setStoreResolved(featureKey, true);
        // Reconciliación de feature (SPEC-11): la hidratación AUTORITATIVA dispara los
        // reconciliadores registrados (idempotentes, diferidos a microtask). SIEMPRE — aunque
        // la entrada no haya cambiado: es la red de seguridad para transiciones ocurridas con
        // la app cerrada (p.ej. el root revirtió en OTRO equipo y este debe resetear su estado
        // BLE local). Features sin reconciliador: no-op.
        scheduleReconcile(featureKey, { entry: next, prev: prevEntry, cause: 'hydration' });
        // Disparador PASIVO de la encuesta de permanencia (C): si el backend resolvió que el
        // comercio ya ADOPTÓ la versión nueva (menu_state='adopted', p.ej. por uso sostenido vía
        // el cron del backoffice), pedimos la encuesta UNA sola vez por (comercio, feature). El
        // guard durable (maybeRequest…) sobrevive a remounts/navegación → no reaparece. Fire-and-
        // forget: nunca bloquea el render ni la hidratación.
        if (next.menu_state === 'adopted') {
          maybeRequestPermanenceSurveyForAdopted(featureKey, commerceId);
        }
      })
      .catch(() => {
        // Degradación segura: nos quedamos con el cache (o control). No rompemos render.
        setStoreResolved(featureKey, true);
      })
      .finally(() => {
        hydratingFor.delete(featureKey);
      });
  }, [featureKey]);

  const emit = useCallback(
    (
      behavior: FeatureBehavior,
      opts: {
        from?: FeatureVariant;
        to?: FeatureVariant;
        trigger?: string;
        experimentId?: string | null;
        payload?: Record<string, unknown>;
        // event_id ESTABLE opcional: cuando el call-site puede dispararse más de una vez para la
        // misma intención (p.ej. adopción 'kept'), pasa un id estable para que el dedupe por id del
        // outbox colapse los reenvíos en un solo evento. Por defecto un ULID nuevo por evento.
        eventId?: string;
      },
    ) => {
      enqueueTelemetry({
        event_id: opts.eventId ?? ulid(),
        feature_key: featureKey,
        behavior,
        // experiment_id: el explícito del call-site GANA y se reenvía VERBATIM —incluido null—
        // para no coercionar a undefined (Seer 14507629). Para el RESTO de behaviors se estampa
        // el del cache vigente AL ENCOLAR (SPEC-6): el evento pertenece a la experiencia que lo
        // generó — un reverted_to drenado del outbox después de que arrancó el experimento
        // siguiente se atribuiría al equivocado si lo resolviera el backend al ingest
        // (recordTelemetry respeta `e.experiment_id ||` activo y NO valida contra BD, por diseño
        // — confirmado por backend). Sin id en cache (pre-hidratación) → se omite la clave y el
        // backend resuelve el activo. Se lee del STORE SNAPSHOT, no del `entry` del closure:
        // emit solo depende de [featureKey]; meter entry desestabilizaría en cascada los
        // callbacks que lo consumen (switchTo/optOut/…).
        ...('experimentId' in opts
          ? { experiment_id: opts.experimentId }
          : (() => {
              const id = getStoreSnapshot(featureKey).entry.experiment_id;
              return id ? { experiment_id: id } : {};
            })()),
        from_variant: opts.from,
        to_variant: opts.to,
        trigger_source: opts.trigger,
        occurred_at: new Date().toISOString(),
        payload: opts.payload,
      });

      // Espejo a GA4 (vía useTracking). Solo behaviors con evento en el catálogo; params no-PII.
      const ga4Name = BEHAVIOR_TO_GA4[behavior];
      if (ga4Name) {
        trackEventRef.current(ga4Name, {
          feature_key: featureKey,
          from_variant: opts.from,
          to_variant: opts.to,
          trigger_source: opts.trigger,
        });
      }
    },
    [featureKey],
  );

  const switchTo = useCallback(
    // `opts.pushInvite` (SPEC-1): el carácter "invitación PUSH" es propiedad del CALL-SITE (la
    // superficie que invita), no del string del trigger. Las superficies del framework (accept()
    // de makeFeatureGateHook) lo pasan true → invitation_accepted se emite estructuralmente para
    // cualquier feature, sin listas de triggers que olvidar. isPushTrigger queda como ruta
    // legacy de menú (menu_modal/menu_shell, call-sites previos al contrato FeatureGate).
    (next: FeatureVariant, trigger = 'menu_shell', opts?: { pushInvite?: boolean }) => {
      const from = entry.variant;
      if (next === from) return;

      // A1-residual (guard de commerce null): sin commerce activo el PUT NO se encola
      // (enqueueSwitch early-returna) pero ANTES igual se escribía el optimista al store/cache,
      // dejando un V2 fantasma sin rastro ni alerta de divergencia (el outbox nunca lo revierte
      // porque nunca hubo item). Si no hay commerce activo, abortamos el switch COMPLETO: ni
      // optimista ni outbox. La variante en pantalla sigue siendo la última confirmada.
      const commerceId = getActiveCommerceId();
      if (!commerceId) return;

      // El comparador distingue tres intenciones que comparten endpoint:
      //   - compare_peek    → mirar la anterior SIN rechazar (variant=control de salida, pero
      //                       sigues siendo treatment: can_revert se preserva, NO se muta menu_state).
      //   - compare_return  → volver a la nueva (cierra el peek).
      //   - revert_commit / cualquier otro switch a control → RECHAZO definitivo.
      const isPeek = trigger === 'compare_peek' && next === 'control';
      const isCompareReturn = trigger === 'compare_return' && next === 'treatment';
      const isCompare = isPeek || isCompareReturn;

      // 1) Optimista: actualizamos cache + store compartido YA (no esperamos red). El write
      // al store notifica a TODAS las instancias → el gate del router re-renderiza y cambia
      // la página al instante, no solo la etiqueta de la card.
      // En un switch DEFINITIVO (aceptar o rechazar), el comercio deja de ser `never_tried`:
      // ya probó. Subimos el estado optimista a `exploring` para que las superficies intrusivas
      // (modal/card) NO reaparezcan en la misma sesión tras revertir (el backend lo confirma
      // luego como `exploring`/`reverted_sticky`). El cooldown/cap durables los gobierna el
      // backend; aquí solo evitamos el badgering inmediato.
      // En un PEEK NO mutamos menu_state ni la membresía: sigues siendo treatment, solo miras.
      const optimisticMenuState = isCompare
        ? entry.menu_state
        : !entry.menu_state || entry.menu_state === 'never_tried'
          ? 'exploring'
          : entry.menu_state;
      const optimistic = toEntry(featureKey, {
        variant: next,
        // Peek: preservamos can_revert=true (sigues pudiendo volver a la nueva). Treatment
        // siempre puede revertir. Rechazo a control: respeta can_revert previo.
        can_revert: next === 'treatment' || isPeek ? true : entry.can_revert,
        ga_forced: entry.ga_forced,
        menu_state: optimisticMenuState,
        experiment_id: entry.experiment_id,
        invite_surface: entry.invite_surface,
        // El chip PULL no cambia con un switch optimista: lo gobierna el backend (lock/GA/evac).
        manual_entry: entry.manual_entry,
        next_eligible_at: entry.next_eligible_at,
        operator_evacuated: entry.operator_evacuated,
        evac_reason: entry.evac_reason,
        // El peek deja compare_active=true (barra sticky). El return lo cierra.
        compare_active: isPeek ? true : isCompareReturn ? false : entry.compare_active,
        // Un switch local no cambia el catálogo de chips: se preserva (igual que manual_entry).
        feedback_options: entry.feedback_options,
        // Escritura OPTIMISTA: el backend aún no confirma este switch (el PUT va por el outbox).
        // confirmed_at=null marca "pendiente" → si el PUT se rechaza/agota, el outbox revierte a la
        // última variante confirmada en vez de dejar treatment fantasma.
        confirmed_at: null,
      });
      writeCache(optimistic);
      setStoreEntry(featureKey, optimistic);
      // Reconciliación (SPEC-11): el switch OPTIMISTA también dispara, con cause='switch' —
      // los reconciliadores deciden si les aplica (el de BLE solo actúa en 'hydration').
      scheduleReconcile(featureKey, { entry: optimistic, prev: entry, cause: 'switch' });

      // 2) Fire-and-forget: PUT + telemetría vía outbox (nunca await en el handler).
      enqueueSwitch({ id: ulid(), featureKey, toVariant: next, fromVariant: from, trigger }, commerceId);
      // El comparador reutiliza reverted_to/switched_to con trigger_source diferenciado.
      emit(next === 'control' ? 'reverted_to' : 'switched_to', { from, to: next, trigger });
      // invitation_accepted SOLO en la adopción-PUSH real (control→treatment vía superficie de
      // invitación). Un 'chip_pull' (canal PULL) o cualquier trigger no-push NO cuenta como
      // adopción-push, así no contamina by_state ni la métrica de aceptación. El carácter push
      // llega por opts.pushInvite (contrato FeatureGate) o por la lista legacy de menú.
      if (next === 'treatment' && from === 'control' && (opts?.pushInvite || isPushTrigger(trigger))) {
        emit('invitation_accepted', { to: next, trigger });
      }
    },
    [entry, featureKey, emit],
  );

  // Rechazo definitivo a la versión anterior: trigger revert_commit (el backoffice aplica
  // backoff/sticky). NO es un peek.
  const revert = useCallback((trigger = 'revert_commit') => switchTo('control', trigger), [switchTo]);

  // Feedback del revert (prompt D1, omitible): adjunta el motivo como un evento PROPIO
  // (`revert_feedback`), NO como un segundo `reverted_to`. La reversión ya quedó contada en su
  // propio `reverted_to` al ejecutar `revert()`; re-emitir `reverted_to` aquí duplicaba el conteo
  // de reversiones en analytics/backoffice (Seer #1043, finding 14509977/1). Fire-and-forget; NO
  // toca la variante (el revert ya se ejecutó). El motivo y el texto libre (truncado, NO-PII)
  // viajan en `payload`; si el backend aún no lee `reason`, lo capturará en paralelo sin romper.
  // Se manda explícito experiment_id para atarlo al experimento.
  const submitRevertFeedback = useCallback(
    (reason: string, text = '', trigger = 'revert_feedback') => {
      emit('revert_feedback', {
        from: 'treatment',
        to: 'control',
        trigger,
        experimentId: entry.experiment_id,
        payload: {
          reason,
          // Texto libre opcional, truncado a 280 para no inflar el payload ni filtrar PII larga.
          ...(text.trim() ? { reason_text: text.trim().slice(0, 280) } : {}),
        },
      });
    },
    [entry.experiment_id, emit],
  );

  // Feedback por USO (E#3, omitible): el operador opinó tras usar la versión nueva. Va por el
  // canal TELEMETRÍA (preserva el payload) como behavior PROPIO 'usage_feedback' — NO es un
  // reverted_to ni un switch: no toca la variante ni cuenta como adopción/reversión. Adjunta
  // `by_user_id` (el operador, a menudo no-root) y `core_action_count` para que el dashboard pueda
  // ponderar la opinión por intensidad de uso. reason_text truncado a 280 (NO-PII). Fire-and-forget.
  const submitUsageFeedback = useCallback(
    (reason: string, text = '', coreActionCount?: number, trigger = 'usage_feedback') => {
      const byUserId = (() => {
        if (typeof window === 'undefined') return undefined;
        try {
          return window.localStorage.getItem('id_user') ?? undefined;
        } catch {
          return undefined;
        }
      })();
      emit('usage_feedback', {
        trigger,
        experimentId: entry.experiment_id,
        payload: {
          reason,
          ...(text.trim() ? { reason_text: text.trim().slice(0, 280) } : {}),
          ...(typeof coreActionCount === 'number' ? { core_action_count: coreActionCount } : {}),
          ...(byUserId ? { by_user_id: byUserId } : {}),
        },
      });
    },
    [entry.experiment_id, emit],
  );

  // Adopción EXPLÍCITA ("Quedarme con la versión nueva"). En treatment, confirma la permanencia:
  // encola un switch a la MISMA variante con trigger 'kept' (el backoffice lo lee como adopción y
  // deja state='adopted' + emite el evento 'adopted'/'kept'). switchTo NO sirve aquí (early-returna
  // cuando next===from); por eso encolamos el PUT directo con un event_id ULID estable (dedup/
  // idempotencia que pide el backoffice). NO escribimos optimista de variante (sigue treatment) ni
  // mutamos menu_state local: el GET autoritativo confirmará 'adopted'. Además abrimos la encuesta
  // de permanencia. Sin commerce activo abortamos (igual que switchTo). No-root: el PUT vuelve
  // REQUIRES_ROOT y el outbox lo descarta — no hay variante que revertir.
  const keepCurrent = useCallback(
    (trigger = 'kept') => {
      // Solo tiene sentido cuando la versión nueva está activa para el comercio.
      if (entry.variant !== 'treatment') return;
      const commerceId = getActiveCommerceId();
      if (!commerceId) return;

      // event_id ESTABLE per-commerce: el CTA "Quedarme" sigue visible en treatment y el popover se
      // reabre, así que keepCurrent puede dispararse más de una vez para el mismo comercio. Cachear
      // el id (primer disparo lo crea, los siguientes lo reusan) hace que el dedupe por id del outbox
      // colapse los reenvíos en uno → ni el PUT 'kept' ni el evento 'switched_to/kept' se duplican.
      const cache = keptEventIdRef.current;
      let eventId = cache.get(commerceId);
      if (!eventId) {
        eventId = ulid();
        cache.set(commerceId, eventId);
      }

      // PUT 'kept' fire-and-forget. to=from=treatment: el outbox lo encola con ese id ULID estable
      // (dedupe por id → encolar dos veces no duplica). El revert del outbox ante fallo terminal es
      // un no-op (from_variant=treatment = la variante actual): no deja V2 fantasma.
      enqueueSwitch({ id: eventId, featureKey, toVariant: 'treatment', fromVariant: 'treatment', trigger }, commerceId);
      // Telemetría del switch (mismo behavior que un switched_to, con trigger_source diferenciado
      // para que el dashboard descomponga adopción explícita vs por tiempo). Mismo event_id estable
      // → el dedupe del outbox también colapsa la telemetría duplicada de re-clicks.
      emit('switched_to', { from: 'treatment', to: 'treatment', trigger, eventId });

      // Abre la encuesta de permanencia (omitible). El shell del menú la renderiza como overlay.
      requestPermanenceSurvey(featureKey);
    },
    [entry.variant, featureKey, emit],
  );

  // Feedback POSITIVO de permanencia (omitible): el comercio adoptó/se quedó con la versión nueva.
  // Va por el canal TELEMETRÍA como behavior 'usage_feedback' con trigger_source ∈ {adopted, kept}
  // (la procedencia vive en el evento, NO en RDS). NO toca la variante ni cuenta como adopción (esa
  // ya la registró el disparador/CTA). reason_text truncado a 280 (NO-PII). Fire-and-forget.
  const submitPermanenceFeedback = useCallback(
    (reason: string, text = '', trigger = 'adopted') => {
      emit('usage_feedback', {
        trigger,
        experimentId: entry.experiment_id,
        payload: {
          reason,
          ...(text.trim() ? { reason_text: text.trim().slice(0, 280) } : {}),
        },
      });
    },
    [entry.experiment_id, emit],
  );

  // "No volver a preguntar": opt_out fire-and-forget. No cambia la variante; el backend la
  // vuelve sticky. Optimista: marcamos invite_surface='none' para que el modal/card no reaparezcan.
  const optOut = useCallback(
    (trigger = 'menu_invitation') => {
      const optimistic = toEntry(featureKey, {
        variant: entry.variant,
        can_revert: entry.can_revert,
        ga_forced: entry.ga_forced,
        menu_state: entry.menu_state === 'never_tried' ? 'exploring' : entry.menu_state,
        experiment_id: entry.experiment_id,
        invite_surface: 'none',
        // opt_out silencia el PUSH pero DEJA el chip disponible (es opt-out del modal/card, NO
        // del chip). El chip solo se oculta con manual_entry_locked, que es otro flag.
        manual_entry: entry.manual_entry,
        next_eligible_at: entry.next_eligible_at,
        operator_evacuated: entry.operator_evacuated,
        evac_reason: entry.evac_reason,
        compare_active: entry.compare_active,
        // opt_out no toca el catálogo de chips: se preserva.
        feedback_options: entry.feedback_options,
        // opt_out NO cambia la variante: conservamos la confirmación previa del backend.
        confirmed_at: entry.confirmed_at,
      });
      writeCache(optimistic);
      setStoreEntry(featureKey, optimistic);

      const commerceId = getActiveCommerceId();
      enqueueOptOut({ id: ulid(), featureKey, trigger }, commerceId);
      emit('opt_out', { trigger });
    },
    [entry, featureKey, emit],
  );

  // "Recordármelo más tarde": snooze fire-and-forget. No cambia la variante; el backend
  // suprime invitaciones hasta now+days. Optimista: invite_surface='none' (no reaparecer ya).
  const snooze = useCallback(
    (days = 7, trigger = 'menu_invitation') => {
      const optimistic = toEntry(featureKey, {
        variant: entry.variant,
        can_revert: entry.can_revert,
        ga_forced: entry.ga_forced,
        menu_state: entry.menu_state,
        experiment_id: entry.experiment_id,
        invite_surface: 'none',
        // snooze suprime solo el PUSH; el chip PULL queda disponible.
        manual_entry: entry.manual_entry,
        next_eligible_at: entry.next_eligible_at,
        operator_evacuated: entry.operator_evacuated,
        evac_reason: entry.evac_reason,
        compare_active: entry.compare_active,
        // snooze no toca el catálogo de chips: se preserva.
        feedback_options: entry.feedback_options,
        // snooze NO cambia la variante: conservamos la confirmación previa del backend.
        confirmed_at: entry.confirmed_at,
      });
      writeCache(optimistic);
      setStoreEntry(featureKey, optimistic);

      const commerceId = getActiveCommerceId();
      enqueueSnooze({ id: ulid(), featureKey, snoozeDays: days, trigger }, commerceId);
    },
    [entry, featureKey],
  );

  const markInvitationShown = useCallback(
    (trigger = 'menu_shell') => {
      // Guard durable por (comercio, feature): idempotente entre remounts del shell. Sin commerce
      // activo no registramos (la telemetría iría sin sujeto) ni marcamos el guard.
      const commerceId = getActiveCommerceId();
      if (!commerceId) return;
      const key = `${commerceId}:${featureKey}`;
      if (invitationShownFor.has(key)) return;
      invitationShownFor.add(key);
      emit('invitation_shown', { trigger });
    },
    [featureKey, emit],
  );

  // Emite behavior='error' cuando la superficie V2 gateada falla (capturado por el error boundary
  // del gate). Fire-and-forget vía outbox; NO rompe el render (el boundary ya cayó al fallback/V1).
  // Se reporta el error siempre que en pantalla esté V2: con variante treatment (adopción real) O
  // en VISTA PREVIA per-usuario (Seer 14512214/0: un operador no-root que previsualiza V2 también
  // renderiza el componente treatment; si crashea, el error debe reportarse, no silenciarse). Para
  // no contaminar el guardrail error_rate del EXPERIMENTO con crashes de preview (el usuario sigue
  // en control, no es parte de la cohorte treatment), marcamos `during_preview` en el payload: el
  // dashboard separa "regresión de la cohorte treatment" de "estabilidad observada en preview".
  const emitError = useCallback(
    (error: unknown, trigger = 'menu_v2_gate') => {
      const duringPreview = isPreviewActive(featureKey);
      const showingTreatment = entry.variant === 'treatment' || duringPreview;
      if (!showingTreatment) return;
      // Payload NO-PII: solo nombre del error y mensaje truncado (sin stack ni datos de usuario).
      const name = error instanceof Error ? error.name : 'UnknownError';
      const message = error instanceof Error && typeof error.message === 'string' ? error.message.slice(0, 200) : '';
      emit('error', {
        from: 'treatment',
        trigger,
        experimentId: entry.experiment_id,
        payload: { error_name: name, error_message: message, ...(duringPreview ? { during_preview: true } : {}) },
      });
    },
    [entry.variant, entry.experiment_id, featureKey, emit],
  );

  // Defensa por cache stale: si el operador evacuó este comercio, ninguna superficie de
  // invitación debe encenderse aunque el invite_surface cacheado diga otra cosa (el server ya
  // manda 'none', esto cubre el race antes de re-hidratar).
  const inviteSurface: InviteSurface = entry.operator_evacuated ? 'none' : entry.invite_surface;

  // Defensa por cache stale (mismo patrón que inviteSurface): si el operador evacuó el comercio,
  // el chip PULL no debe encenderse aunque el manual_entry cacheado diga 'available' (el server
  // ya manda 'hidden', esto cubre el race antes de re-hidratar).
  const manualEntry: ManualEntry = entry.operator_evacuated ? 'hidden' : entry.manual_entry;

  return {
    variant: entry.variant,
    canRevert: entry.can_revert,
    gaForced: entry.ga_forced,
    menuState: entry.menu_state,
    experimentId: entry.experiment_id,
    inviteSurface,
    manualEntry,
    operatorEvacuated: entry.operator_evacuated,
    evacReason: entry.evac_reason,
    compareActive: entry.compare_active,
    feedbackOptions: entry.feedback_options,
    isEligibleInvitation:
      entry.variant === 'control' && !entry.ga_forced && !entry.operator_evacuated && Boolean(entry.experiment_id),
    resolved,
    switchTo,
    revert,
    submitRevertFeedback,
    submitUsageFeedback,
    keepCurrent,
    submitPermanenceFeedback,
    optOut,
    snooze,
    markInvitationShown,
    emitError,
  };
}

export default useFeatureVersion;
